// ABOUTME: The feed's small decisions: a listing item read as the card the job feed already draws, the first page of the
// ABOUTME: listings section in the shape the job feed's own query caches, and which sections this app knows how to draw.
import { PAGE_SIZE } from 'modules/jobListings/constants';
import type {
  JobListingCard,
  JobListingsPage
} from 'modules/jobListings/types';
import { FeedSection, ListingItem, ListingsSection } from './types';

/** A listing item of kind JOB as the flat card the job feed draws; the badges are already on it as fields. */
export const listingItemToCard = (item: ListingItem): JobListingCard => {
  const badge = <T extends ListingItem['badges'][number]['type']>(type: T) =>
    item.badges.find(entry => entry.type === type);
  const fit = badge('WEEK_FIT');
  const raised = badge('EARNINGS_RAISED');
  const places = badge('PLACES_LEFT');
  return {
    refId: item.refId,
    title: item.title,
    publishedAt: item.publishedAt,
    companyName: item.company.name,
    companyLogoImageRefId: item.company.logoImageRefId,
    myApplication: item.myApplication,
    ...item.job,
    remote: item.job.remote ?? item.job.workplaceMode === 'REMOTE',
    weekFit: fit?.type === 'WEEK_FIT' ? fit.value : undefined,
    earningsRaised:
      raised?.type === 'EARNINGS_RAISED' && raised.value !== undefined
        ? { previousHourlyRate: raised.value }
        : undefined,
    placesLeft: places?.type === 'PLACES_LEFT' ? places.value : undefined,
    weekendAsked: badge('WEEKEND_WORK') ? true : undefined
  };
};

/** The listings section as the job feed's first page, so its query starts from what the feed already brought. */
export const firstListingsPage = (
  section: ListingsSection
): JobListingsPage => ({
  data: section.items
    .filter(item => item.kind === 'JOB')
    .map(listingItemToCard),
  meta: {
    pagination: {
      page: 0,
      size: PAGE_SIZE,
      // Only `next` says there is more; the search then goes on at page 1 of the same criteria.
      totalPages: section.next
        ? Math.max(2, Math.ceil(section.total / PAGE_SIZE))
        : 1,
      totalElements: section.total
    }
  }
});

const KNOWN: FeedSection['kind'][] = [
  'EMPLOYMENT',
  'APPLICATION_PROCESS',
  'LISTINGS'
];

/** A section kind this app version does not know is skipped, so pro-core can ship new ones before the app draws them. */
export const knownSections = (sections: FeedSection[]): FeedSection[] =>
  sections.filter(section => KNOWN.includes(section.kind));
